import React from 'react';
import LegalLayout from '../components/LegalLayout';

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms & Conditions"
      lastUpdated="February 2026"
      seoTitle="Terms & Conditions | PlanningSearch"
      seoDescription="Terms and conditions governing use of PlanningSearch — UK planning application intelligence platform for builders, architects and tradespeople."
      canonical="https://planningsearch.com/terms"
    >
      <h2>1. Introduction</h2>
      <p>
        These Terms & Conditions ("Terms") govern your access to and use of the PlanningSearch website and services
        (collectively, the "Service"), operated by PlanningSearch Ltd ("we", "us", or "our"), a company registered in
        England and Wales.
      </p>
      <p>
        By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, please do not use the Service.
      </p>

      <h2>2. Description of Service</h2>
      <p>
        PlanningSearch provides a platform for discovering, analysing, and acting upon UK planning applications. Our
        Service includes but is not limited to: interactive map and list search, AI-powered intelligence reports,
        estimated project valuations, physical letter sending, and email alert notifications.
      </p>

      <h2>3. User Accounts</h2>
      <p>
        To access certain features of the Service, you must create an account. You are responsible for maintaining
        the confidentiality of your login credentials and for all activities that occur under your account.
      </p>
      <p>
        You agree to provide accurate, current, and complete information during registration and to keep your account
        information updated. We reserve the right to suspend or terminate accounts that violate these Terms.
      </p>

      <h2>4. Subscription Plans & Payments</h2>
      <p>
        PlanningSearch offers both free and paid subscription plans. Paid plans are billed on a monthly or annual basis
        as selected at checkout. All prices are displayed in GBP and are inclusive of VAT where applicable.
      </p>
      <p>
        You may cancel your subscription at any time from your account dashboard. Cancellation takes effect at the end
        of your current billing period, and no partial refunds will be issued for the remaining days of that period.
      </p>
      <p>
        We reserve the right to modify pricing with 30 days' advance notice to existing subscribers. Continued use of
        the Service after a price change constitutes acceptance of the new price.
      </p>

      <h2>5. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
        <li>Scrape, crawl, or systematically extract data from the Service without our written consent</li>
        <li>Attempt to gain unauthorised access to any part of the Service or its related systems</li>
        <li>Use the Service to send unsolicited bulk communications (spam)</li>
        <li>Resell, redistribute, or sublicense access to the Service or its data</li>
        <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity</li>
      </ul>

      <h2>6. Data Accuracy</h2>
      <p>
        Planning application data displayed on PlanningSearch is sourced from public council records and third-party
        providers. While we take reasonable steps to ensure accuracy, we do not warrant that the data is complete,
        current, or error-free. See our <a href="/disclaimer">Disclaimer</a> for further details.
      </p>

      <h2>7. AI-Generated Content</h2>
      <p>
        Intelligence reports, cost estimates, opportunity scores, and AI-generated letter content are produced using
        artificial intelligence and are provided for informational purposes only. They do not constitute professional
        advice (financial, legal, or otherwise) and should not be solely relied upon for business decisions.
      </p>

      <h2>8. Physical Letter Service</h2>
      <p>
        Our letter-sending service dispatches physical mail to planning applicants on your behalf via third-party
        postal providers. We are not responsible for postal delays, delivery failures, or the recipient's response
        to your correspondence. Letters must comply with applicable laws and must not contain threatening, defamatory,
        or misleading content.
      </p>

      <h2>9. Intellectual Property</h2>
      <p>
        All content, design, code, and branding on the Service are the intellectual property of PlanningSearch Ltd
        unless otherwise stated. You may not reproduce, modify, or distribute any part of the Service without prior
        written consent.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, PlanningSearch Ltd shall not be liable for any indirect, incidental,
        special, consequential, or punitive damages, including but not limited to loss of profits, data, or business
        opportunities, arising from your use of the Service.
      </p>
      <p>
        Our total liability for any claim arising from the Service shall not exceed the total amount paid by you to
        us in the 12 months preceding the claim.
      </p>

      <h2>11. Termination</h2>
      <p>
        We may suspend or terminate your access to the Service at our discretion, without notice, if we believe you
        have violated these Terms or engaged in conduct that is harmful to other users or the Service.
      </p>

      <h2>12. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. We will notify registered users of material changes via email.
        Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms.
      </p>

      <h2>13. Governing Law</h2>
      <p>
        These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes
        arising from these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
      </p>

      <h2>14. Contact</h2>
      <p>
        If you have questions about these Terms, please contact us at{' '}
        <a href="mailto:legal@planningsearch.com">legal@planningsearch.com</a>.
      </p>
    </LegalLayout>
  );
}
